import React, { useEffect, useMemo, useState, useCallback, createContext, useRef } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

// Navigation Context
const NavigationContext = createContext(null);

// Navigation utilities
const normalizeRole = (role) => {
  if (!role) return "student";
  return role.toLowerCase().replace(/\s+/g, "-");
};

const getDashboardPath = (role) => {
  const normalized = normalizeRole(role);
  return `/${normalized}/dashboard`;
};

const getModulePath = (role, moduleId) => {
  const normalized = normalizeRole(role);
  return `/${normalized}/module/${moduleId}`;
};

const parseHash = (hash) => {
  if (!hash || hash === "#" || hash === "#/") {
    return { route: "dashboard", role: null, moduleId: null };
  }
  
  const path = hash.replace(/^#/, "").replace(/^\//, "");
  const parts = path.split("/").filter(Boolean);
  
  if (parts.length === 0) {
    return { route: "dashboard", role: null, moduleId: null };
  }
  
  if (parts.length === 2 && parts[1] === "dashboard") {
    return { route: "dashboard", role: parts[0], moduleId: null };
  }
  
  if (parts.length === 3 && parts[1] === "module") {
    return { route: "module", role: parts[0], moduleId: parts[2] };
  }
  
  return { route: "dashboard", role: null, moduleId: null };
};

// Navigation Hook (available for future use in nested components)
// const useNavigation = () => {
//   const context = useContext(NavigationContext);
//   if (!context) {
//     throw new Error("useNavigation must be used within NavigationProvider");
//   }
//   return context;
// };

// BackButton Component
const BackButton = ({ userRole, onNavigateToDashboard, navigate, navigationHistory, className = "" }) => {
  const handleBack = useCallback(() => {
    // First try: go back one step in SPA history (navigate(-1) equivalent)
    if (navigationHistory && navigationHistory.current && navigationHistory.current.length > 1) {
      // Remove current path from history
      navigationHistory.current.pop();
      // Get previous path
      const previousPath = navigationHistory.current[navigationHistory.current.length - 1];
      
      if (previousPath && navigate) {
        navigate(previousPath);
        return;
      }
    }
    
    // Fallback: try browser history
    if (window.history.length > 1) {
      try {
        window.history.back();
        // If back() doesn't work, fallback after a short delay
        setTimeout(() => {
          // Check if we're still on the same page (back didn't work)
          if (onNavigateToDashboard) {
            // Only fallback if we're still on a module page
            const hash = window.location.hash || "";
            if (hash.includes("/module/")) {
              onNavigateToDashboard();
            }
          }
        }, 100);
        return;
      } catch (e) {
        // Continue to dashboard fallback
      }
    }
    
    // Final fallback: navigate to dashboard
    if (onNavigateToDashboard) {
      onNavigateToDashboard();
    }
  }, [onNavigateToDashboard, navigate, navigationHistory]);

  return (
    <button
      onClick={handleBack}
      className={`flex items-center gap-2 text-ghs-blue hover:text-ghs-gold transition-colors font-bold text-[11px] uppercase tracking-wider ${className}`}
      aria-label="Go back"
    >
      <i className="fa-solid fa-chevron-left"></i>
      <span>Back</span>
    </button>
  );
};

// Role-based module definitions
const agsRoles = {
  Student: [
    { id: "s1", title: "Academic Dashboard", icon: "📚", desc: "Haftalık ders programı, gerçek zamanlı not takibi, AP (Advanced Placement) ve OSSD ders içerikleri ile eğitmen geri bildirimlerine anlık erişim paneli." },
    { id: "s2", title: "My Global Portfolio", icon: "📂", desc: "Grade 9'dan mezuniyete kadar tüm projeleriniz, sertifikalarınız, sosyal sorumluluk belgeleriniz ve akademik kanıtlarınızın toplandığı 4 yıllık stratejik dijital depo." },
    { id: "s3", title: "GHS Digital Archive", icon: "🏛️", desc: "Uluslararası diploma müfredatları, global e-kitap kütüphanesi, akademik araştırma makaleleri ve geçmiş video ders kayıtlarından oluşan devasa kaynak merkezi." },
    { id: "s4", title: "Academic Calendar", icon: "🗓️", desc: "Sınav tarihleri, Global Ties seminerleri, üniversite başvuru günleri, uluslararası okul etkinlikleri ve tatil takviminin kişiselleştirilmiş görünümü." },
    { id: "s5", title: "IDP Tracker", icon: "🧠", desc: "Intellectual Development Program (IDP) kapsamında belirlenen kişisel hedefler, kritik okuma listeleri ve multidisipliner gelişim izleme ekranı." },
    { id: "s6", title: "AI Writing Coach", icon: "✍️", desc: "Uluslararası standartlarda akademik makaleler ve essayler için yapay zeka destekli gramer, üslup iyileştirme ve intihal kontrolü geri bildirim sistemi." },
    { id: "s7", title: "Live Streaming Hub", icon: "🎥", desc: "Global kampüsler arası senkronize canlı ders bağlantıları, uluslararası konferanslar ve sanal sınıf işbirliği protokolleri merkezi." },
    { id: "s8", title: "Student Wellbeing", icon: "❤️", desc: "Psikolojik danışmanlık, mentorluk, sosyal-duygusal destek kaynakları ve mental sağlık yönetim araçlarına 7/24 erişim." },
  ],
  Teacher: [
    { id: "t1", title: "Class Management", icon: "🧑‍🏫", desc: "Sınıf listeleri, yoklama yönetimi, dijital ödev atama ve uluslararası diploma (AP/OSSD) notlandırma skalası kontrol merkezi." },
    { id: "t2", title: "Portfolio Audit", icon: "🔍", desc: "Öğrenci kanıtlarını inceleme, küresel akreditasyon standartlarına göre (WCCI) onaylama ve video/sesli geri bildirim sağlama arayüzü." },
    { id: "t3", title: "Resource Center", icon: "📤", desc: "Ders videoları, PDF materyaller, etkileşimli soru bankaları ve yapay zeka destekli sınav içerikleri yükleme ve kategorizasyon alanı." },
    { id: "t4", title: "Student Performance", icon: "📊", desc: "Bireysel öğrenci bazlı veya sınıf geneli akademik gelişim analitiği, risk altındaki öğrenci tespiti ve AI destekli başarı tahmin raporları." },
    { id: "t5", title: "Curriculum Mapping", icon: "🗺️", desc: "Ders planlarının GHS Global standartları, UNESCO sürdürülebilir eğitim hedefleri ve uluslararası diploma gereklilikleriyle senkronizasyonu." },
    { id: "t6", title: "Teacher Training", icon: "📈", desc: "GHS kurumsal sertifikasyon süreçleri, hizmet içi eğitim kayıtları, global eğitmen ağı dökümanları ve profesyonel gelişim portfolyosu." },
    { id: "t7", title: "Feedback Bot", icon: "🤖", desc: "Öğrencilere otomatik akademik geri bildirim sağlayan yapay zeka asistanı yapılandırması ve veri seti yönetimi." },
  ],
  Admin: [
    { id: "a1", title: "System Administration", icon: "🧑‍💻", desc: "TODO: Global RBAC Control - Tüm sistem mimarisi, kullanıcı rolleri, yetkilendirme protokolleri ve veritabanı erişim seviyelerinin yönetimi." },
    { id: "a2", title: "Security Management", icon: "🛡️", desc: "TODO: Cyber Security - Uçtan uca veri şifreleme katmanları, güvenlik günlükleri, MFA ve KVKK/GDPR uyum yönetim merkezi." },
    { id: "a3", title: "AI Configuration", icon: "🧠", desc: "TODO: AI Core Settings - Sistemdeki yapay zeka motorlarının öğrenme parametreleri, API anahtarları ve akademik geri bildirim algoritmalarının ince ayarları." },
  ],
  Parent: [
    { id: "p1", title: "Progress Monitor", icon: "📈", desc: "Çocuğunuzun notları, ödev tamamlama oranları, devam durumu ve öğretmenlerin akademik yorumlarını anlık olarak takip edin." },
    { id: "p2", title: "Portfolio Access", icon: "📁", desc: "Çocuğunuzun 4 yıl boyunca biriktirdiği tüm başarıların, projelerin, sertifikaların ve sosyal sorumluluk kanıtlarının dijital arşivi." },
    { id: "p3", title: "Calendar & Events", icon: "🗓️", desc: "Veli-öğretmen toplantıları, sınav dönemleri, üniversite tanıtım günleri, GHS etkinlikleri ve tüm okul duyurularına erişim." },
    { id: "p4", title: "Financial Portal", icon: "💳", desc: "Ödeme geçmişi, güncel taksit planları, faturalar, burs durum raporları ve eğitim sigortası bilgilerine güvenli erişim." },
    { id: "p5", title: "Direct Messaging", icon: "💬", desc: "Okul yönetimi, rehberlik servisi ve öğretmenlerle hızlı, güvenli ve kurumsal yazılı iletişim kanalı." },
  ],
  "School Coordinator": [
    { id: "c1", title: "Campus Directory", icon: "🧑‍🤝‍🧑", desc: "Kampüs bünyesindeki tüm öğrenci, veli ve öğretmen veritabanının merkezi yönetim, kayıt ve iletişim paneli." },
    { id: "c2", title: "Academic Quality Audit", icon: "📋", desc: "Eğitim kalitesinin, müfredat sunumunun ve ölçme-değerlendirme süreçlerinin AGS global standartlarına uygunluk denetimi." },
    { id: "c3", title: "Global Ties Planning", icon: "📢", desc: "Global Ties seminerleri, uluslararası konuk konuşmacı organizasyonları ve kampüsler arası etkinlik koordinasyon merkezi." },
    { id: "c4", title: "Accreditation Hub", icon: "📜", desc: "Uluslararası diploma programlarının (AP, OSSD, AHSD) resmi kayıt süreçleri, kredi transferleri ve mezuniyet kriterleri takibi." },
    { id: "c5", title: "Audit Records", icon: "📋", desc: "Resmi teftiş kayıtları, kalite kontrol raporları, akademik başarı grafikleri ve kurum içi performans denetim arşivi." },
  ],
  "Member School Administrator": [
    { id: "ma1", title: "School Identity", icon: "🏫", desc: "Okul kurumsal bilgileri, tesis yönetimi, kampüs bazlı görsel ayarlar, logo ve kurumsal vizyon parametreleri yönetimi." },
    { id: "ma2", title: "Legal & Compliance", icon: "⚖️", desc: "Resmi sözleşmeler, yerel eğitim mevzuatı uyumu, denetim kayıtları ve kurumsal yasal döküman arşivi." },
    { id: "ma3", title: "Staff HR", icon: "👥", desc: "Kampüs eğitmen kadrosu ve personelin atama, yetkilendirme, bordro takibi ve performans izleme süreçleri." },
    { id: "ma4", title: "Activity Reports", icon: "📝", desc: "Haftalık operasyonel raporlar, tesis kullanım verileri, etkinlik maliyet analizi ve kampüs doluluk oranları." },
  ],
  "System Administrator": [
    { id: "sa1", title: "Global RBAC Control", icon: "🧑‍💻", desc: "Tüm sistem mimarisi, kullanıcı rolleri, yetkilendirme protokolleri ve veritabanı erişim seviyelerinin yönetimi." },
    { id: "sa2", title: "Cyber Security", icon: "🛡️", desc: "Uçtan uca veri şifreleme katmanları, güvenlik günlükleri, MFA (Çok Faktörlü Kimlik Doğrulama) ve KVKK/GDPR uyum yönetim merkezi." },
    { id: "sa3", title: "AI Core Settings", icon: "🧠", desc: "Sistemdeki yapay zeka motorlarının öğrenme parametreleri, API anahtarları ve akademik geri bildirim algoritmalarının ince ayarları." },
    { id: "sa4", title: "Integration Desk", icon: "🔗", desc: "Zoom, CRM, ERP, dış API servisleri ve kurumsal e-posta sistemlerinin global entegrasyon yönetimi." },
    { id: "sa5", title: "DevOps Logs", icon: "☁️", desc: "Bulut altyapı durumu, sunucu yanıt süreleri, trafik analizi ve sistem hata logları (Error Logs) anlık izleme ekranı." },
  ],
  "AGS Accounting Personnel": [
    { id: "ac1", title: "Revenue Analytics", icon: "💰", desc: "Partner okullar, global kampüsler ve bireysel öğrenci bazlı gelir projeksiyonları, nakit akışı ve finansal sağlık raporları." },
    { id: "ac2", title: "Invoice Automation", icon: "🧾", desc: "Global faturalandırma süreçleri, vergi (KDV/VAT) beyannameleri ve kurumlar arası mali transferlerin takibi." },
    { id: "ac3", title: "Scholarship Fund", icon: "🎓", desc: "Global burs havuzu tahsisleri, finansal yardım kriterleri yönetimi ve indirim yapılarının kampüs bazlı yapılandırması." },
    { id: "ac4", title: "Audit Trails", icon: "🔒", desc: "Tüm finansal işlemlerin silinemez işlem kayıtları, muhasebe logları ve global denetim izleri." },
  ],
  "Guest User": [
    { id: "g1", title: "Demo Materials", icon: "🌐", desc: "GHS derslerinden kesitler, demo müfredat içerikleri, interaktif örnekler ve sistem tanıtım videoları." },
    { id: "g2", title: "Information Desk", icon: "ℹ️", desc: "Admissions (Kayıt) süreçleri, global kampüs listesi, okul ücretleri ve burs olanakları hakkında detaylı rehber." },
    { id: "g3", title: "Virtual Tour", icon: "🧭", desc: "Global kampüslerin 360 derece dijital navigasyonu ve fiziksel olanakların interaktif tanıtımı." },
  ],
  "Partner Account": [
    { id: "pa1", title: "Joint Projects", icon: "🤝", desc: "Uluslararası akademik partnerlerle yürütülen ortak projeler, araştırma grupları ve işbirliği çalışma alanları." },
    { id: "pa2", title: "Shared Data", icon: "📄", desc: "Kurumlar arası paylaşıma açılmış araştırma raporları, stratejik döküman havuzu ve ortak müfredat geliştirme alanı." },
    { id: "pa3", title: "Event Calendar", icon: "🗓️", desc: "Ortak yürütülen uluslararası etkinliklerin, seminerlerin ve değişim programlarının koordinasyon takvimi." },
  ],
};

// Utility functions
const getTimeGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 18) return "Good Afternoon";
  return "Good Evening";
};

const getAvailableRoles = (userRole) => {
  // TODO: Implement role hierarchy and permissions
  // For now, admins can view all roles, others see their own
  if (userRole === "Admin" || userRole === "System Administrator") {
    return Object.keys(agsRoles);
  }
  return [userRole].filter(Boolean);
};

// Role resolver for dashboard paths
const resolveRoleForPath = (userRole) => {
  // TODO: Implement proper role resolution from Firestore or auth token
  // For now, use the provided userRole or default to Student
  return userRole || "Student";
};

// Component: GHS Logo
const GHSLogo = ({ light = false }) => (
  <a
    href="https://theglobalhighschool.org"
    target="_blank"
    rel="noopener noreferrer"
    className="flex flex-col leading-tight group select-none"
  >
    <span className={`font-heading font-bold tracking-tight ${light ? "text-white" : "text-ghs-blue"} text-xl`}>
      The Global <span className="text-ghs-gold">High School</span>
    </span>
    <span className={`text-[0.6rem] uppercase tracking-[0.2em] font-body mt-1 ${light ? "text-white/70" : "text-gray-500"}`}>
      Est. Atlantic Global Scholars
    </span>
  </a>
);

// Component: Stat Widget
const StatWidget = ({ title, value, icon }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-soft p-6 flex items-center justify-between hover:shadow-mega transition">
    <div>
      <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">{title}</p>
      <p className="text-2xl font-heading font-bold text-ghs-blue">{value}</p>
    </div>
    <div className="text-2xl opacity-30">{icon}</div>
  </div>
);

// Component: Exam Report
const ExamReport = () => (
  <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden animate-in zoom-in duration-700">
    <div className="bg-[#002366] p-8 text-white text-center">
      <div className="text-[#C5A059] text-[10px] font-bold uppercase tracking-[0.3em] mb-2">AGS Official Assessment</div>
      <h4 className="text-2xl font-heading font-bold uppercase">Proficiency Certificate</h4>
    </div>
    <div className="p-10 text-center">
      <div className="inline-block p-6 rounded-full border-4 border-[#C5A059] mb-6">
        <span className="text-5xl font-bold text-[#002366]">C1</span>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Advanced</p>
      </div>
      <div className="space-y-4 mb-10">
        <div className="flex justify-between items-center text-xs border-b pb-2">
          <span className="text-gray-500 font-medium">Reading Comprehension</span>
          <span className="text-[#002366] font-bold">92%</span>
        </div>
        <div className="flex justify-between items-center text-xs border-b pb-2">
          <span className="text-gray-500 font-medium">Grammar & Syntax</span>
          <span className="text-[#002366] font-bold">88%</span>
        </div>
      </div>
      <button className="w-full bg-[#002366] text-white py-4 rounded-xl font-bold uppercase tracking-widest text-[10px] hover:bg-[#C5A059] transition-all flex items-center justify-center gap-3">
        <i className="fa-solid fa-file-pdf"></i> Download Official Performance Report
      </button>
    </div>
  </div>
);

// Component: Calendar Content
const CalendarContent = () => (
  <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
    <div className="bg-ghs-blue p-3 text-white text-center font-bold text-[10px] tracking-widest uppercase">January 2026</div>
    <div className="grid grid-cols-7 gap-1 p-4">
      {Array.from({ length: 31 }).map((_, i) => (
        <div key={i} className="aspect-square flex items-center justify-center text-[10px] border border-gray-50 hover:bg-ghs-gold/10 transition cursor-pointer">{i + 1}</div>
      ))}
    </div>
  </div>
);

// Component: Digital Archive Content
const DigitalArchiveContent = () => {
  const categories = [
    { title: "International Curricula", icon: "📜", count: "12 Programs", tags: ["AP", "OSSD", "IGCSE"], desc: "Full academic frameworks and assessment guides." },
    { title: "Global E-Library", icon: "📖", count: "450+ Books", tags: ["Fiction", "Academic", "Science"], desc: "Access to world-class digital textbooks and literature." },
    { title: "Research Papers", icon: "🔬", count: "1.2k Articles", tags: ["STEM", "Social", "AI"], desc: "Peer-reviewed journals and student research archives." },
    { title: "Lecture Vault", icon: "🎥", count: "85 Hours", tags: ["Live Sessions", "Tutorials"], desc: "Recorded sessions from global campus instructors." }
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((cat, i) => (
          <div key={i} className="group bg-white border border-gray-100 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer">
            <div className="flex justify-between items-start mb-4">
              <span className="text-4xl">{cat.icon}</span>
              <span className="bg-ghs-blue/5 text-ghs-blue text-[9px] font-bold px-2 py-1 rounded-full uppercase tracking-tighter">
                {cat.count}
              </span>
            </div>
            <h4 className="text-ghs-blue font-bold text-lg mb-1">{cat.title}</h4>
            <p className="text-[11px] text-gray-400 mb-4 leading-relaxed">{cat.desc}</p>
            <div className="flex gap-2">
              {cat.tags.map(tag => (
                <span key={tag} className="text-[8px] font-bold text-ghs-gold border border-ghs-gold/20 px-2 py-0.5 rounded italic">#{tag}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className="bg-ghs-blue rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="relative z-10">
          <p className="text-ghs-gold text-[10px] font-bold uppercase tracking-widest mb-2">Editor's Choice</p>
          <h5 className="text-xl font-heading font-bold mb-2">The Impact of AI on Global Education 2026</h5>
          <button className="text-[10px] font-bold uppercase underline hover:text-ghs-gold transition-colors">Read Publication</button>
        </div>
        <div className="absolute right-[-20px] top-[-20px] text-9xl opacity-10 rotate-12">🧪</div>
      </div>
    </div>
  );
};

// Component: Dashboard Content
const DashboardContent = () => (
  <div className="space-y-6 animate-in fade-in duration-500">
    <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 shadow-sm">
      <h4 className="font-heading font-bold text-ghs-blue mb-4 text-xs uppercase tracking-widest text-ghs-gold">Weekly Schedule</h4>
      <div className="grid grid-cols-5 gap-2">
        {["Math", "Physics", "English", "History", "Art"].map((c, i) => (
          <div key={i} className="bg-white p-3 rounded border border-gray-100 text-center shadow-sm">
            <p className="text-ghs-blue font-bold text-[10px]">{c}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-ghs-blue p-6 rounded-xl text-white shadow-lg">
      <p className="text-[10px] uppercase font-bold text-ghs-gold mb-1 tracking-widest">GPA (Current Cycle)</p>
      <p className="text-3xl font-heading font-bold">3.88 / 4.00</p>
    </div>
  </div>
);

// Component: Module Content Renderer
const ModuleContent = ({ module }) => {
  if (!module) return null;

  // Academic Dashboard
  if (module.title.includes("Academic Dashboard") || module.id === "s1") {
    return <DashboardContent />;
  }

  // Academic Calendar
  if (module.title.includes("Calendar") || module.id === "s4") {
    return <CalendarContent />;
  }

  // AI Writing Coach / Exam Report
  if (module.title.includes("Coach") || module.id === "s6") {
    return <ExamReport />;
  }

  // GHS Digital Archive
  if (module.title.includes("Digital Archive") || module.id === "s3") {
    return <DigitalArchiveContent />;
  }

  // Default placeholder
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
        <div className="text-ghs-gold text-2xl mb-2">{module.icon}</div>
        <h4 className="font-heading font-bold text-ghs-blue">{module.title}</h4>
        <p className="text-xs text-gray-500 mt-2">Institutional materials for {module.title} are being synchronized.</p>
    </div>
  </div>
);
};

// Component: More Menu (Mobile Dropdown)
const MoreMenu = ({ isOpen, onClose, navigate, viewRole, onCalendarClick, onSupportClick }) => {
  const menuItems = [
    { label: "Calendar", icon: "🗓️", action: () => {
      if (onCalendarClick) {
        onCalendarClick();
      }
      onClose();
    }},
    { label: "Support", icon: "💬", action: () => {
      if (onSupportClick) {
        onSupportClick();
      }
      onClose();
    }},
  ];

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/20 z-40 lg:hidden"
        onClick={onClose}
      />
      <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px] lg:hidden">
        {menuItems.map((item, idx) => (
          <button
            key={idx}
            onClick={item.action}
            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-ghs-blue font-bold text-[11px] uppercase tracking-wider transition-colors"
          >
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </>
  );
};

// Component: App Shell (Layout Wrapper) - Reusable Layout Component
const AppShell = ({ user, userData, viewRole, setViewRole, children, currentTime, onSignOut, navigate, currentRoute, navigationHistory }) => {
  const isAdmin = userData?.role === "Admin" || userData?.role === "System Administrator";
  const availableRoles = useMemo(() => getAvailableRoles(userData?.role), [userData?.role]);
  const userRole = resolveRoleForPath(userData?.role);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const handleRoleChange = useCallback((e) => {
    const newRole = e.target.value;
    setViewRole(newRole);
    // Navigate to new role's dashboard using navigate() (no page reload)
    if (navigate) {
      navigate(getDashboardPath(newRole));
    }
  }, [setViewRole, navigate]);

  const handleDashboardClick = useCallback((e) => {
    e.preventDefault();
    // Use navigate() for client-side navigation (no page reload)
    if (navigate) {
      navigate(getDashboardPath(viewRole));
    }
  }, [navigate, viewRole]);

  const handleBackToDashboard = useCallback(() => {
    // Use navigate() for client-side navigation (no page reload)
    if (navigate) {
      navigate(getDashboardPath(viewRole));
    }
  }, [navigate, viewRole]);

  const handleCalendarClick = useCallback((e) => {
    e.preventDefault();
    const calendarModule = agsRoles[viewRole]?.find(m => m.title.includes("Calendar") || m.id === "s4");
    if (calendarModule && navigate) {
      navigate(getModulePath(viewRole, calendarModule.id));
    }
  }, [navigate, viewRole]);

  const handleSupportClick = useCallback((e) => {
    e.preventDefault();
    // TODO: Implement support route - for now navigate to dashboard
    if (navigate) {
      navigate(getDashboardPath(viewRole));
    }
  }, [navigate, viewRole]);

  const showBackButton = currentRoute?.route === "module";

  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-800 app-container">
      {/* Utility Bar */}
      <div className="hidden lg:block bg-ghs-blue text-white text-[13px] border-b border-white/10">
        <div className="container mx-auto px-4 md:px-6 py-2.5 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4 md:gap-6 flex-wrap">
            <a 
              href="https://theglobalhighschool.org/?modal=store"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-ghs-gold transition font-medium"
            >
              Store
            </a>
            <a 
              href="https://theglobalhighschool.org/?modal=contact"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-ghs-gold transition font-medium"
            >
              Contact
            </a>
            <button className="hover:text-ghs-gold transition" title="Search">
              <i className="fa-solid fa-magnifying-glass" />
            </button>
            <div className="h-3 w-px bg-white/30 hidden md:block" />
            <span className="font-bold text-ghs-gold cursor-default text-xs tracking-wider">
              EN | <span className="text-white/60">TR</span>
            </span>
          </div>

          <div className="flex items-center gap-4 md:gap-6 whitespace-nowrap">
            <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold">Global Ops</div>
            <div className="text-[11px] font-bold">
              LDN{" "}
              {currentTime.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })}
              <span className="text-ghs-gold/60 mx-2">|</span>
              IST{" "}
              {currentTime.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white sticky top-0 z-40 w-full py-4 md:py-5 border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          {/* Logo */}
    <div className="flex-shrink-0 min-w-0 max-w-full">
      <GHSLogo />
    </div>

          {/* Navigation - Persistent Top Navigation */}
          <nav className="hidden lg:flex flex-grow justify-center px-4">
            <div className="flex space-x-4 md:space-x-6 items-center">
              {/* Back Button - Left side when on module page */}
              {showBackButton && (
                <BackButton 
                  userRole={userRole} 
                  onNavigateToDashboard={handleBackToDashboard}
                  navigate={navigate}
                  navigationHistory={navigationHistory}
                />
              )}
              {/* Quick Links */}
              <button
                onClick={handleDashboardClick}
                className="nav-link font-bold text-ghs-blue text-[11px] uppercase tracking-wider whitespace-nowrap hover:text-ghs-gold transition-colors"
              >
                Dashboard
              </button>
              <button
                onClick={handleCalendarClick}
                className="nav-link font-bold text-ghs-blue text-[11px] uppercase tracking-wider whitespace-nowrap hover:text-ghs-gold transition-colors"
              >
                Calendar
              </button>
              <button
                onClick={handleSupportClick}
                className="nav-link font-bold text-ghs-blue text-[11px] uppercase tracking-wider whitespace-nowrap hover:text-ghs-gold transition-colors"
              >
                Support
              </button>
            </div>
      </nav>

          {/* Mobile Navigation Bar - Always visible on mobile */}
          <div className="lg:hidden w-full flex items-center justify-between gap-2 md:gap-4 pt-2 border-t border-gray-100">
            {/* Left: Back + Dashboard */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {showBackButton && (
                <BackButton 
                  userRole={userRole} 
                  onNavigateToDashboard={handleBackToDashboard}
                  navigate={navigate}
                  navigationHistory={navigationHistory}
                />
              )}
              <button
                onClick={handleDashboardClick}
                className="flex items-center gap-1 md:gap-2 text-ghs-blue hover:text-ghs-gold transition-colors font-bold text-[10px] md:text-[11px] uppercase tracking-wider"
              >
                <i className="fa-solid fa-home text-xs"></i>
                <span className="hidden sm:inline">Dashboard</span>
              </button>
            </div>
            {/* Right: More Menu */}
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                className="flex items-center gap-1 text-ghs-blue hover:text-ghs-gold transition-colors font-bold text-[11px] uppercase tracking-wider px-2 py-1"
                aria-label="More menu"
              >
                <span className="text-lg">⋯</span>
                <span className="hidden sm:inline text-[10px]">More</span>
              </button>
              <MoreMenu 
                isOpen={moreMenuOpen}
                onClose={() => setMoreMenuOpen(false)}
                navigate={navigate}
                viewRole={viewRole}
                onCalendarClick={handleCalendarClick}
                onSupportClick={handleSupportClick}
              />
            </div>
    </div>

          {/* User Actions */}
          <div className="flex items-center gap-3 md:gap-4 flex-shrink-0 w-full md:w-auto justify-between md:justify-end">
            {isAdmin && availableRoles.length > 1 && (
 <select
                className="bg-white border border-gray-200 rounded px-3 py-2 text-[11px] font-bold text-ghs-blue uppercase tracking-wider focus:outline-none focus:border-ghs-gold"
  value={viewRole}
                onChange={handleRoleChange}
              >
                {availableRoles.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      )}

            {user && (
              <div className="hidden xl:flex flex-col text-right min-w-0 max-w-[160px]">
          <span className="font-heading font-bold text-ghs-blue text-[13px] truncate" title={userData?.name}>
            {userData?.name || "Academic User"}
          </span>
          <span className="text-[9px] font-extrabold uppercase tracking-[0.2em] text-ghs-gold truncate">
            {viewRole}
          </span>
              </div>
            )}

      {user && (
        <button
          onClick={onSignOut}
                className="bg-ghs-gold text-ghs-blue px-3 md:px-4 py-2 rounded font-bold text-[10px] uppercase tracking-wider hover:bg-ghs-blue hover:text-white transition shadow-sm whitespace-nowrap"
        >
          Sign out
        </button>
      )}
    </div>
  </div>
</header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-ghs-dark text-gray-400 text-[13px] py-10 md:py-14 border-t border-gray-900">
        <div className="container mx-auto px-4 md:px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="opacity-90">
            <GHSLogo light />
          </div>
          <div className="text-[11px] text-gray-600 uppercase tracking-wider">
            © 2026 The Global High School. All Rights Reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

// Component: Login Page
const LoginPage = ({ onLogin, error }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(e);
  };

    return (
      <div className="min-h-screen bg-ghs-dark">
        <div
          className="relative min-h-screen bg-cover bg-center flex items-center"
          style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=1950&q=80')",
          }}
        >
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,35,102,.92), rgba(0,35,102,.65))" }} />
          <div className="relative z-10 w-full">
          <div className="container mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="text-white">
                <span className="inline-block py-1.5 px-4 border border-white/20 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] mb-8 bg-white/5 backdrop-blur-md text-ghs-gold">
                  Secure Ecosystem Gateway
                </span>
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-heading font-bold leading-tight mb-6">
                  Redefining Secondary Education
                <span className="block font-heading italic font-light text-ghs-gold/90 mt-3 text-2xl md:text-3xl lg:text-5xl">
                    for a Global Future
                  </span>
                </h1>
              <p className="text-base md:text-lg text-white/80 max-w-xl leading-relaxed">
                  Student, Parent, Teacher and Institutional access is managed via the central AGS LMS.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-mega border border-white/20 overflow-hidden">
              <div className="p-6 md:p-8 bg-ghs-blue text-white flex items-center justify-between">
                  <div>
                    <span className="text-ghs-gold text-xs font-bold uppercase tracking-widest block mb-1">Portal</span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold">Login</h2>
                  <p className="text-xs md:text-sm text-white/70 mt-1">Authorised access only. All activity is logged.</p>
                  </div>
                  <div className="hidden sm:block">
                    <GHSLogo light />
                  </div>
                </div>

              <div className="p-6 md:p-8">
                  {error && (
                    <div className="bg-red-50 text-red-700 p-4 rounded-lg text-[12px] font-bold mb-6 border border-red-100">
                      {error}
                    </div>
                  )}

                <form className="space-y-4" onSubmit={handleSubmit}>
                    <input
                      name="email"
                      type="email"
                      placeholder="Institutional Email"
                      className="w-full bg-gray-50 border border-gray-200 rounded p-3 focus:outline-none focus:border-ghs-blue text-sm"
                      required
                    />
                    <input
                      name="pass"
                      type="password"
                      placeholder="Password"
                      className="w-full bg-gray-50 border border-gray-200 rounded p-3 focus:outline-none focus:border-ghs-blue text-sm"
                      required
                    />
                    <button
                      className="w-full bg-ghs-blue text-white font-bold uppercase text-sm py-4 rounded hover:bg-ghs-gold hover:text-ghs-blue transition shadow-soft"
                      type="submit"
                    >
                      Enter Portal
                    </button>
                  </form>

                  <div className="mt-6 text-[11px] text-gray-500">
                    Need access? Use your institution-issued credentials.
                  </div>
                </div>
              </div>
            </div>

          {/* Stats Strip */}
          <div className="mt-12 md:mt-16 bg-ghs-blue text-white py-8 md:py-10 border-t border-white/10 border-b-4 border-ghs-gold">
            <div className="container mx-auto px-4 md:px-6 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 text-center">
                <div>
                <div className="text-3xl md:text-4xl font-heading text-ghs-gold">Global</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/70 mt-1">Curriculum Alignment</div>
                </div>
                <div>
                <div className="text-3xl md:text-4xl font-heading text-ghs-gold">Dual</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/70 mt-1">Diploma Pathways</div>
                </div>
                <div>
                <div className="text-3xl md:text-4xl font-heading text-ghs-gold">WCCI</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/70 mt-1">Aligned Framework</div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    );
};

// Component: Dashboard Page
const DashboardPage = ({ userData, viewRole, selectedModule, navigate, userRole }) => {
  const currentModules = useMemo(() => agsRoles[viewRole] || agsRoles.Student, [viewRole]);

  const handleModuleClick = useCallback((module) => {
    if (navigate) {
      navigate(getModulePath(viewRole, module.id));
    }
  }, [navigate, viewRole]);

  const handleBackToDashboard = useCallback(() => {
    if (navigate) {
      navigate(getDashboardPath(viewRole));
    }
  }, [navigate, viewRole]);

  return (
    <div className="bg-ghs-light min-h-screen app-content">
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-10">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 md:mb-10">
            <StatWidget title="Academic Cycle" value="2025-26 S2" icon="🏛️" />
            <StatWidget title="Compliance" value="Verified" icon="🛡️" />
            <StatWidget title="Attendance" value="98.5%" icon="🗓️" />
            <StatWidget title="Global Hub" value="Active" icon="🌐" />
          </div>

        {/* Welcome Section */}
        <div className="mb-8 md:mb-10 border-b border-gray-100 pb-6 md:pb-8">
            <div className="flex items-center gap-2 mb-2">
              <span className="h-px w-6 bg-ghs-gold"></span>
              <span className="text-ghs-gold font-bold text-[9px] uppercase tracking-[0.3em]">{viewRole} Portal</span>
            </div>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-ghs-blue">
              {getTimeGreeting()}, <span className="italic font-light text-gray-400">{userData?.name?.split(' ')[0] || "Scholar"}</span>
            </h2>
          </div>

        {/* Module Content or Module Grid */}
          {selectedModule ? (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in duration-500">
              <div className="grid grid-cols-1 lg:grid-cols-3">
              {/* Side Panel */}
              <div className="bg-ghs-blue p-6 md:p-8 text-white">
                {/* Note: Primary navigation is in AppShell header. This is a convenience button. */}
                <button 
                  onClick={handleBackToDashboard} 
                  className="text-[10px] uppercase font-bold text-ghs-gold hover:underline flex items-center gap-2 mb-6 transition-colors"
                  aria-label="Back to Dashboard"
                >
                    <i className="fa-solid fa-chevron-left"></i> Back to Dashboard
                  </button>
                <div className="text-4xl md:text-5xl mt-4 md:mt-8 mb-4">{selectedModule.icon}</div>
                <h3 className="text-xl md:text-2xl font-heading font-bold mb-4">{selectedModule.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{selectedModule.desc}</p>
                </div>

              {/* Content Area */}
              <div className="lg:col-span-2 p-6 md:p-8 bg-white">
                <ModuleContent module={selectedModule} />
                </div>
              </div>
            </div>
          ) : (
          /* Module Cards Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {currentModules.map(m => (
              <button 
                key={m.id} 
                onClick={() => handleModuleClick(m)} 
                className="group bg-white p-5 md:p-6 rounded-2xl border border-gray-100 text-left hover:shadow-xl transition-all duration-300 relative overflow-hidden"
              >
                  <div className="text-3xl mb-4">{m.icon}</div>
                  <h3 className="text-sm font-bold text-ghs-blue mb-2 uppercase tracking-tight group-hover:text-ghs-gold">{m.title}</h3>
                  <p className="text-[11px] text-gray-400 line-clamp-2">{m.desc}</p>
                  <div className="absolute bottom-0 left-0 h-1 w-0 bg-ghs-gold group-hover:w-full transition-all duration-500"></div>
                </button>
              ))}
            </div>
          )}
       </div>
      </div>
  );
};

// Main App Component
export default function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewRole, setViewRole] = useState("Student");
  const [selectedModule, setSelectedModule] = useState(null);
  const [error, setError] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentRoute, setCurrentRoute] = useState({ route: "dashboard", role: null, moduleId: null });
  const navigationHistory = useRef([]);

  // Auth state handler - race condition free
  useEffect(() => {
    let isMounted = true;
    let unsubscribe = null;

    const initializeAuth = async () => {
      try {
        unsubscribe = onAuthStateChanged(auth, async (authUser) => {
          if (!isMounted) return;

          try {
            if (authUser) {
              const userDoc = await getDoc(doc(db, "users", authUser.uid));
              const data = userDoc.exists() ? userDoc.data() : { name: "Academic User", role: "Student" };
              
              if (isMounted) {
                setUser(authUser);
                setUserData(data);
                setViewRole(data.role || "Student");
                setError("");
              }
            } else {
              if (isMounted) {
                setUser(null);
                setUserData(null);
                setViewRole("Student");
                setSelectedModule(null);
                setError("");
              }
            }
          } catch (err) {
            console.error("Error loading user data:", err);
            if (isMounted) {
              setError("User profile could not be loaded (Firestore).");
            }
          } finally {
            if (isMounted) {
              setLoading(false);
            }
          }
        });
      } catch (err) {
        console.error("Auth initialization error:", err);
        if (isMounted) {
          setLoading(false);
          setError("Authentication system error.");
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Clock update
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Navigation function
  const navigate = useCallback((path) => {
    const hash = `#${path}`;
    
    // Track navigation history for back button
    if (!navigationHistory.current.includes(path)) {
      navigationHistory.current.push(path);
    }
    
    // Use pushState for client-side navigation (no page reload)
    window.history.pushState({ path }, "", hash);
    const route = parseHash(hash);
    setCurrentRoute(route);
    
    // Update selectedModule based on route
    if (route.route === "module" && route.moduleId) {
      const roleModules = agsRoles[viewRole] || agsRoles.Student;
      const module = roleModules.find(m => m.id === route.moduleId);
      setSelectedModule(module || null);
    } else {
      setSelectedModule(null);
    }
  }, [viewRole]);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = (e) => {
      const hash = window.location.hash || "#/";
      const path = hash.replace(/^#/, "");
      const route = parseHash(hash);
      setCurrentRoute(route);
      
      // Update navigation history when browser back/forward is used
      if (path && !navigationHistory.current.includes(path)) {
        navigationHistory.current.push(path);
      }
      
      // Update selectedModule based on route
      if (route.route === "module" && route.moduleId) {
        const roleModules = agsRoles[viewRole] || agsRoles.Student;
        const module = roleModules.find(m => m.id === route.moduleId);
        setSelectedModule(module || null);
      } else {
        setSelectedModule(null);
      }
    };

    window.addEventListener("popstate", handlePopState);
    
    // Initialize route from URL
    const hash = window.location.hash || "#/";
    const path = hash.replace(/^#/, "");
    const route = parseHash(hash);
    setCurrentRoute(route);
    
    // Initialize navigation history
    if (path && path !== "/") {
      navigationHistory.current = [path];
    } else {
      navigationHistory.current = [];
    }
    
    // If we have a module in URL, set it
    if (route.route === "module" && route.moduleId) {
      const roleModules = agsRoles[viewRole] || agsRoles.Student;
      const module = roleModules.find(m => m.id === route.moduleId);
      if (module) {
        setSelectedModule(module);
      }
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [viewRole]);

  // Initialize navigation on login
  useEffect(() => {
    if (user && userData) {
      const role = userData.role || "Student";
      const dashboardPath = getDashboardPath(role);
      
      // Only navigate if we're not already on a valid route
      if (!window.location.hash || window.location.hash === "#" || window.location.hash === "#/") {
        navigate(dashboardPath);
      }
    }
  }, [user, userData, navigate]);

  // Login handler
  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setError("");
    const email = e.target.email.value;
    const password = e.target.pass.value;

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Auth state will be updated by onAuthStateChanged
    } catch (err) {
      console.error("Login error:", err);
      setError("Authentication Failed. Check credentials.");
    }
  }, []);

  // Sign out handler
  const handleSignOut = useCallback(() => {
    signOut(auth).catch((err) => {
      console.error("Sign out error:", err);
      setError("Failed to sign out. Please try again.");
    });
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-ghs-dark flex items-center justify-center">
        <div className="text-white text-center">
          <div className="text-2xl font-heading font-bold mb-4">Loading...</div>
          <div className="text-sm text-white/70">Initializing secure connection</div>
        </div>
      </div>
    );
  }

  // Not authenticated - show login
  if (!user) {
    return <LoginPage onLogin={handleLogin} error={error} />;
  }

  // Authenticated - show dashboard
  const userRole = resolveRoleForPath(userData?.role);
  
  return (
    <NavigationContext.Provider value={{ navigate, currentRoute }}>
      <AppShell
        user={user}
        userData={userData}
        viewRole={viewRole}
        setViewRole={setViewRole}
        currentTime={currentTime}
        onSignOut={handleSignOut}
        navigate={navigate}
        currentRoute={currentRoute}
        navigationHistory={navigationHistory}
      >
        <DashboardPage
          userData={userData}
          viewRole={viewRole}
          selectedModule={selectedModule}
          navigate={navigate}
          userRole={userRole}
        />
    </AppShell>
    </NavigationContext.Provider>
  );
}
